import type { ReactNode } from 'react';
import { Tooltip } from '@/components/UI';
import type { OptimizerJobRow, OptimizerResultRow } from './types';
import { type optimizerMaterializationProgress, formatSignedPct, toNumber } from './helpers';
import JobDiagnostics from './JobDiagnostics';
import JobRunProgress from './JobRunProgress';

interface JobStatsGridProps {
    selectedJob: OptimizerJobRow;
    selectedJobProgress: ReturnType<typeof optimizerMaterializationProgress> | null;
    results: OptimizerResultRow[];
    resultsTotalCount: number;
    selectedJobUsesOptionMetric: boolean;
    holdoutInfoText: string | null;
}

const ACTIVE_STATUSES: OptimizerJobRow['status'][] = ['queued', 'materializing', 'optimizing', 'validating'];

interface StatTileProps {
    label: string;
    value: ReactNode;
    caption?: ReactNode;
    tooltip?: string;
}

const StatTile = ({ label, value, caption, tooltip }: StatTileProps) => {
    const labelNode = <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>;

    return (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 px-4 py-3">
            {tooltip ? <Tooltip content={tooltip}>{labelNode}</Tooltip> : labelNode}
            <div className="text-xl font-semibold text-white mt-0.5">{value}</div>
            {caption && <div className="text-xs text-zinc-500 mt-0.5">{caption}</div>}
        </div>
    );
};

const VALIDATION_SETUP_TOOLTIP = [
    'Rules are scored on a holdout slice of entries the optimizer never searched on.',
    'The embargo skips entries near the train/holdout boundary to prevent leakage.',
    'Winsorizing caps extreme outcomes at the given percentile so single trades cannot dominate.'
].join('\n');

/**
 * State-aware job summary: a progress card while the job runs, headline stat tiles once it
 * completes, and pipeline diagnostics tucked behind a disclosure in every state.
 */
const JobStatsGrid = ({
    selectedJob,
    selectedJobProgress,
    results,
    resultsTotalCount,
    selectedJobUsesOptionMetric,
    holdoutInfoText
}: JobStatsGridProps) => {
    const isActive = ACTIVE_STATUSES.includes(selectedJob.status);
    const isCompleted = selectedJob.status === 'completed';
    const firstResult = results.length > 0 ? results[0] : null;

    const funnelTooltip = [
        `${selectedJob.combinationsEvaluated?.toLocaleString() ?? '?'} rule combinations evaluated`,
        `${selectedJob.candidatesBeforeCV?.toLocaleString() ?? '?'} passed the initial search`,
        `${selectedJob.candidatesAfterCV?.toLocaleString() ?? '?'} survived cross-validation`
    ].join('\n');

    return (
        <>
            <div className="text-xs text-zinc-400 mb-3">
                Feature set: <span className="font-mono">{selectedJob.featureSetVersion}</span>
                {selectedJob.featureSetFeatureCount != null && ` · ${selectedJob.featureSetFeatureCount} source fields`}
            </div>
            {isActive && selectedJobProgress && <JobRunProgress selectedJob={selectedJob} selectedJobProgress={selectedJobProgress} />}

            {isCompleted && (
                <>
                    <div className="grid grid-cols-2 gap-3 mb-2 lg:grid-cols-4">
                        <StatTile
                            caption={
                                selectedJob.totalEntities != null
                                    ? `across ${selectedJob.totalEntities.toLocaleString()} entities`
                                    : undefined
                            }
                            label="Signals"
                            value={selectedJob.totalEntries?.toLocaleString() ?? '-'}
                        />
                        <StatTile
                            caption={
                                results.length > 0
                                    ? `showing ${results.length.toLocaleString()} of ${resultsTotalCount.toLocaleString()}`
                                    : undefined
                            }
                            label="Validated rules"
                            tooltip={funnelTooltip}
                            value={selectedJob.candidatesAfterCV?.toLocaleString() ?? '-'}
                        />
                        <StatTile
                            caption="per signal"
                            label="Baseline avg"
                            tooltip="Average P&L across all signals before any rule is applied — what every rule's lift is measured against."
                            value={firstResult ? formatSignedPct(toNumber(firstResult.baselineAvgProfit)) : '-'}
                        />
                        {selectedJobUsesOptionMetric && (
                            <StatTile
                                caption="per executed option"
                                label="Option baseline"
                                tooltip="Average P&L across executed baseline option trades, before any rule is applied."
                                value={
                                    firstResult && firstResult.optionBaselineAvgProfit != null
                                        ? formatSignedPct(toNumber(firstResult.optionBaselineAvgProfit))
                                        : '-'
                                }
                            />
                        )}
                    </div>
                    {holdoutInfoText && (
                        <div className="mb-3 text-xs text-zinc-500">
                            <Tooltip content={VALIDATION_SETUP_TOOLTIP}>
                                <span className="cursor-default underline decoration-dotted decoration-zinc-700 underline-offset-2">
                                    {holdoutInfoText}
                                </span>
                            </Tooltip>
                        </div>
                    )}
                </>
            )}

            <JobDiagnostics defaultExpanded={isActive || selectedJob.status === 'failed'} selectedJob={selectedJob} />
        </>
    );
};

export default JobStatsGrid;
